import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';
import {
  DebounceTimer,
  LoadingStatus,
  ToastThemes,
} from '../../../../constants/common';
import { getFormattedValues } from '../../../../utils/formatter';
import { showToast } from '../../../../utils/common.util';
import TryAgain from '../../../molecules/ErrorState/error.component';
import CircularLoader from '../../../../atoms/CircularLoader/circular-loader';
import { Box, Button, IconButton } from '@mui/material';
import { DateRangePro } from '../../../molecules/DateRange/DateRangePro';
import {
  InfoOutlined,
  SettingsBackupRestoreOutlined,
} from '@mui/icons-material';
import ListView from '../../../../atoms/DataList/ListView';
import TDSConfigtModalMap from './tds-modal-map';
import { GeoTagging } from '../../../../services/geoTagging';
import {
  permissionsObj,
  ViewAccess,
} from '../../../OnBoarding/Organization/RolesAndPermissions/constants/roles-permissions.constants';
import ApiSelectTag from '../../../../atoms/Select/ApiSelectTag';

const AllTDSConfig = () => {
  const { currentUserRoleId } = useSelector((state) => state.main);
  const { compBenPermission } = useSelector((state) => state.modulePermissions);
  const navigate = useNavigate();

  const [checkedItems, setCheckedItems] = useState([]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(-1);
  const [status, setStatus] = useState('BOTH');

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState([null, null]);
  const searchQuery = searchParams?.get('q');
  const debouncedVal = useDebounce(searchQuery, DebounceTimer);

  useEffect(async () => {
    if (currentUserRoleId) {
      setLoading(LoadingStatus.Loading);
      await new GeoTagging()
        .tdsConfigurationUserList(
          currentUserRoleId,
          searchQuery,
          selectedDate[0]
            ? getFormattedValues('YYYY-MM-DD', selectedDate[0])
            : null,
          selectedDate[1]
            ? getFormattedValues('YYYY-MM-DD', selectedDate[1])
            : null
        )
        .then((res) => {
          setLoading(LoadingStatus.Success);
          setList(res);
        })
        .catch((err) => {
          setLoading(LoadingStatus.Failure);
          showToast(err?.response?.data?.message, ToastThemes.error);
          throw err?.response?.data?.message;
        });
    }
  }, [currentUserRoleId, debouncedVal, selectedDate]);

  useEffect(() => {
    setCheckedItems([]);
  }, [list]);

  const filteredList = list?.filter((item) => {
    if (status === 'new') return item.isNewTaxRegime === true;
    if (status === 'old') return item.isNewTaxRegime === false;
    return true; // For BOTH
  });

  const rowData = filteredList?.map((items) => {
    const {
      id,
      displayName,
      panNumber,
      isNewTaxRegime,
      employeeId,
      taxRegimeUpdatedAt,
      taxRegimeUpdateAllowed,
    } = items;
    return {
      empId: employeeId || '',
      name: displayName,
      pan: panNumber,
      regime: isNewTaxRegime ? 'New Regime' : 'Old Regime',
      regimeAt: taxRegimeUpdatedAt
        ? getFormattedValues('DD/MMM/YYYY hh:mm a', taxRegimeUpdatedAt)
        : '',
      action:
        compBenPermission?.['tds-config']?.[permissionsObj.allowEdit] &&
        taxRegimeUpdateAllowed ? (
          <IconButton onClick={() => navigate({ pathname: `${id}` })}>
            <InfoOutlined sx={{ color: 'primary.main80' }} />
          </IconButton>
        ) : (
          { hide: true }
        ),
      id: {
        value: { id, isNewTaxRegime, taxRegimeUpdateAllowed },
        hide: true,
      },
    };
  });

  const isDisabledReset = () => {
    return selectedDate?.[0] === null && selectedDate?.[1] === null;
  };

  const resetFilters = () => {
    setSelectedDate([null, null]);
  };

  const isDisabledCheckbox = (item) => {
    if (!item?.id?.value?.taxRegimeUpdateAllowed) return true;

    if (checkedItems.length === 0) return false;
    const selectedRegime = checkedItems[0]?.isNewTaxRegime;
    return item?.id?.value?.isNewTaxRegime !== selectedRegime;
  };

  const handleDropDown = (event) => {
    const { value, name } = event.target;
    setStatus(value?.id);
  };

  if (loading === LoadingStatus.Failure) {
    return <TryAgain />;
  }
  return (
    <>
      <CircularLoader show={loading === LoadingStatus.Loading} />
      <Box sx={{ backgroundColor: 'white' }}>
        <Box display="flex" justifyContent="space-between">
          <Box mt={0.5} mb={1} p={2}>
            <ApiSelectTag
              defaultValue={'Select Regime'}
              fullWidth
              size="small"
              name="benefitId"
              value={status}
              onchange={handleDropDown}
              color={true}
              dropDownList={[
                { id: 'BOTH', name: 'ALL' },
                { id: 'new', name: 'New Regime' },
                { id: 'old', name: 'Old Regime' },
              ]}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end" gap={2} mb={1} p={2}>
            <DateRangePro
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
            />
            <Button
              startIcon={<SettingsBackupRestoreOutlined />}
              sx={{ textTransform: 'capitalize', mt: -1.5 }}
              disabled={isDisabledReset()}
              onClick={resetFilters}
            >
              Clear
            </Button>
            {compBenPermission?.['tds-config']?.viewAccess !==
              ViewAccess.MY_ACCESS && (
              <Box pr={1}>
                <TDSConfigtModalMap
                  checkedItems={checkedItems}
                  currentUserRoleId={currentUserRoleId}
                  searchQuery={searchQuery}
                  selectedDate={selectedDate}
                  setList={setList}
                />
              </Box>
            )}
          </Box>
        </Box>
        <ListView
          headCells={[
            {
              id: 'empId',
              label: 'Employee ID',
            },
            {
              id: 'name',
              label: 'Employee Name',
            },
            {
              id: 'pan',
              label: 'PAN Number',
            },
            {
              id: 'regime',
              label: 'Tax Regime',
            },
            {
              id: 'regimeAt',
              label: 'Updated At',
              dataType: 'date',
              dateFormat: 'DD/MMM/YYYY hh:mm a',
            },
          ]}
          rowsData={rowData}
          showSerialNo={true}
          showCheckbox={true}
          setCheckedItems={setCheckedItems}
          checkedItems={checkedItems}
          allowSelectAll={status !== 'BOTH'}
          showAddIcon={false}
          isDisabledCheckbox={(item) => isDisabledCheckbox(item)}
        />
      </Box>
    </>
  );
};

export default AllTDSConfig;
